import {
  type FilterObject,
  isAndCondition,
  isOrCondition,
} from '@moment/trading-types/filter-expression'
import { z } from 'zod'

import {
  DEFAULT_CONSTRAINT_PRIORITY,
  isValidConstraintPriority,
} from '@/utils/constraints/constraint-priority'

/**
 * Shared across every constraint schema that validates a priority, so the
 * accepted range and the message cannot drift apart between them.
 */
export const constraintPrioritySchema = z
  .number()
  .refine(
    isValidConstraintPriority,
    'Constraint priority must be a whole number of -1 or above, where -1 never relaxes',
  )
  .nullable()
  .default(DEFAULT_CONSTRAINT_PRIORITY)

/**
 * Utility function to validate that a filter is not empty
 * @param filter The filter object to validate
 * @param constraintType String identifier for the constraint type (used in the error message)
 * @returns Validation result with appropriate error messages
 */
export function validateFilterNotEmpty(
  filter: FilterObject,
  constraintType: string,
): {
  valid: boolean
  error?: string
  errors?: string[]
} {
  const errors: string[] = []

  // Check if filter is an empty object
  if (Object.keys(filter).length === 0) {
    errors.push(
      `${constraintType} filter cannot be empty. Please specify at least one condition.`,
    )
    return {
      valid: false,
      error: errors[0],
      errors,
    }
  }

  // Check if filter has valid field conditions (with non-empty values)
  const hasValidFieldConditions = Object.entries(filter).some(
    ([key, condition]) => {
      // Skip 'and' and 'or' keys
      if (key === 'and' || key === 'or') return false

      // Check if the condition has a non-empty value
      if (typeof condition === 'object') {
        const values = Object.values(condition as Record<string, unknown>)
        return values.some(
          (value) =>
            value !== null &&
            value !== undefined &&
            value !== '' &&
            !(Array.isArray(value) && value.length === 0),
        )
      }

      return false
    },
  )

  // Check nested conditions in 'and' or 'or' arrays
  let hasNestedConditions = false

  if (isOrCondition(filter) && filter.or.length > 0) {
    hasNestedConditions = true
  }

  if (isAndCondition(filter) && filter.and.length > 0) {
    hasNestedConditions = true
  }

  if (!hasValidFieldConditions && !hasNestedConditions) {
    errors.push(
      `${constraintType} filter requires at least one valid field condition with non-empty values`,
    )
  }

  return {
    valid: errors.length === 0,
    error: errors.length > 0 ? errors[0] : undefined,
    errors: errors.length > 0 ? errors : undefined,
  }
}
